import React from "react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import SEO from "./SEO";
import Footer from "./Footer";

export default function PrivacyPage() {
  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Privacy Policy | One80Labs",
    description:
      "One80Labs Privacy Policy - Learn how we collect, use, and protect your personal information for AI training, development, and fractional project management services in Omaha, Nebraska.",
    url: "https://one80labs.com/privacy",
    mainEntity: {
      "@type": "Article",
      headline: "Privacy Policy | One80Labs",
      description:
        "Privacy Policy for One80Labs AI training, development, and fractional project management services.",
      author: {
        "@type": "Organization",
        name: "One80Labs",
      },
      publisher: {
        "@type": "Organization",
        name: "One80Labs",
        address: {
          "@type": "PostalAddress",
          streetAddress: "4383 Nicholas Street #302",
          addressLocality: "Omaha",
          addressRegion: "NE",
          postalCode: "68181",
          addressCountry: "US",
        },
      },
      datePublished: "2025-01-08",
      dateModified: "2025-01-08",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    logo: "https://one80labs.com/logo.png",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3616",
      contactType: "customer service",
      email: "hello@one80labs.com",
      areaServed: "US",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://one80labs.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Privacy Policy",
        item: "https://one80labs.com/privacy",
      },
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "One80Labs",
    url: "https://one80labs.com",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://one80labs.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    telephone: "+1-402-915-3616",
    email: "hello@one80labs.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651675,
      longitude: -95.9397187,
    },
    openingHours: "Mo-Fr 09:00-17:00",
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
      },
      {
        "@type": "State",
        name: "Nebraska",
      },
      {
        "@type": "State",
        name: "Iowa",
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training & Project Management Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Training",
            description: "Custom AI training for businesses and non-profits",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Fractional Project Management",
            description: "Part-time project management services",
          },
        },
      ],
    },
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nick Kramer",
    jobTitle: "AI Productivity Specialist & Technology Educator",
    worksFor: {
      "@type": "Organization",
      name: "One80Labs",
    },
    knowsAbout: [
      "AI Implementation",
      "Project Management",
      "Non-profit Technology",
      "Omaha Business",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy | One80Labs - AI Training & Fractional PM Services"
        description="One80Labs Privacy Policy - Learn how we collect, use, and protect your personal information for AI training, development, and fractional project management services in Omaha, Nebraska."
        keywords={[
          "privacy policy",
          "One80Labs privacy",
          "data protection Omaha",
          "AI training privacy",
          "fractional PM privacy",
          "Nebraska privacy policy",
          "Midwest data protection",
          "GDPR compliance",
          "CCPA compliance",
          "data security",
        ]}
        image="https://one80labs.com/logo.png"
        article={true}
        author="One80Labs"
        publishedTime="2025-01-08T00:00:00.000Z"
        modifiedTime="2025-01-08T00:00:00.000Z"
        section="Legal"
        tags={[
          "privacy policy",
          "data protection",
          "GDPR",
          "CCPA",
          "legal",
          "Omaha",
          "Nebraska",
        ]}
      />

      <Navigation />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1
              className="text-4xl font-bold text-gray-900 mb-4"
              id="main-heading"
            >
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600" role="doc-subtitle">
              Effective Date: January 8, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 mb-8">
            At One80Labs, your privacy is important to us. This Privacy Policy
            explains how we collect, use, and protect your personal information
            when you visit our website, engage with our AI training, AI
            development, or fractional project management services, or interact
            with us in any way.
          </p>

          <p className="text-lg text-gray-700 mb-8">
            We are based in{" "}
            <Link to="/contact" className="text-[#F98B3D] hover:underline">
              Omaha, Nebraska
            </Link>
            , and proudly serve clients across the Midwest. By using our{" "}
            <Link to="/" className="text-[#F98B3D] hover:underline">
              website
            </Link>{" "}
            or services, you agree to the terms of this Privacy Policy.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="information-collection"
          >
            1. Information We Collect
          </h2>
          <p className="text-gray-700 mb-4">
            We may collect the following types of information:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>
              <strong>Contact Information</strong> – Name, email address, phone
              number, mailing address.
            </li>
            <li>
              <strong>Business Information</strong> – Company name, role/title,
              industry.
            </li>
            <li>
              <strong>Service Preferences</strong> – Areas of interest such as
              AI training, AI tool development, or project management.
            </li>
            <li>
              <strong>Technical Data</strong> – IP address, browser type, device
              information, pages visited, and referring URLs.
            </li>
            <li>
              <strong>Communication Data</strong> – Messages sent via contact
              forms, surveys, or email.
            </li>
          </ul>

          <p className="text-gray-700 mb-4">
            We collect this information when you:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Fill out a contact form or request a consultation.</li>
            <li>Subscribe to our newsletter or marketing emails.</li>
            <li>Register for an AI training session or workshop.</li>
            <li>Request a Free AI Audit through our website.</li>
            <li>Engage with us on social media or other marketing channels.</li>
          </ul>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="information-usage"
          >
            2. How We Use Your Information
          </h2>
          <p className="text-gray-700 mb-4">We use your information to:</p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Provide and deliver our services.</li>
            <li>Respond to your inquiries and requests.</li>
            <li>Customize training and AI solutions for your business.</li>
            <li>Improve our website and offerings based on user feedback.</li>
            <li>
              Send updates, promotions, and service announcements (you can opt
              out anytime).
            </li>
            <li>
              Fulfill legal obligations and maintain compliance with applicable
              laws.
            </li>
          </ul>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="information-sharing"
          >
            3. Sharing Your Information
          </h2>
          <p className="text-gray-700 mb-4">
            We do not sell your personal data. We may share information with
            trusted third-party service providers who help us operate our
            business, such as:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Email and Marketing Platforms (e.g., Resend, Mailchimp).</li>
            <li>Analytics Providers (e.g., Google Analytics).</li>
            <li>Payment Processors (if applicable).</li>
            <li>Event and Scheduling Tools (e.g., Calendly).</li>
          </ul>
          <p className="text-gray-700 mb-6">
            All third parties are required to maintain the confidentiality and
            security of your information.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="data-retention"
          >
            4. Data Retention
          </h2>
          <p className="text-gray-700 mb-6">
            We retain your personal information only for as long as necessary to
            fulfill the purposes described in this Privacy Policy, or as
            required by law.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="your-rights"
          >
            5. Your Rights
          </h2>
          <p className="text-gray-700 mb-4">
            Depending on your location (including GDPR and CCPA jurisdictions),
            you may have rights to:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Access, update, or delete your personal information.</li>
            <li>Request a copy of the data we hold about you.</li>
            <li>Opt-out of marketing communications.</li>
            <li>Restrict or object to our processing of your data.</li>
          </ul>
          <p className="text-gray-700 mb-6">
            To exercise your rights, contact us at{" "}
            <a
              href="mailto:hello@one80labs.com"
              className="text-[#F98B3D] hover:underline"
            >
              hello@one80labs.com
            </a>
            .
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="security"
          >
            6. Security
          </h2>
          <p className="text-gray-700 mb-6">
            We use industry-standard security measures to protect your data,
            including SSL encryption and secure hosting. While no system is 100%
            secure, we take all reasonable steps to safeguard your information.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="cookies-tracking"
          >
            7. Cookies and Tracking
          </h2>
          <p className="text-gray-700 mb-4">
            Our website uses cookies and similar tracking technologies to:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Improve website performance.</li>
            <li>Analyze traffic patterns.</li>
            <li>Personalize user experiences.</li>
          </ul>
          <p className="text-gray-700 mb-6">
            You can adjust your browser settings to disable cookies, but some
            site features may not function properly.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="third-party-links"
          >
            8. Third-Party Links
          </h2>
          <p className="text-gray-700 mb-6">
            Our site may link to third-party websites. We are not responsible
            for the privacy practices or content of those websites.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="policy-changes"
          >
            9. Changes to This Privacy Policy
          </h2>
          <p className="text-gray-700 mb-6">
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with a revised "Effective Date."
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="contact-us"
          >
            10. Contact Us
          </h2>
          <p className="text-gray-700 mb-4">
            If you have any questions about this Privacy Policy or our data
            practices, contact us:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <p className="text-gray-700 font-semibold">One80Labs</p>
            <p className="text-gray-700">Omaha, Nebraska</p>
            <p className="text-gray-700">
              📧 Email:{" "}
              <a
                href="mailto:hello@one80labs.com"
                className="text-[#F98B3D] hover:underline"
              >
                hello@one80labs.com
              </a>
            </p>
            <p className="text-gray-700">
              🌐 Website:{" "}
              <a
                href="https://www.one80labs.com"
                className="text-[#F98B3D] hover:underline"
              >
                www.one80labs.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />

      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(privacySchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(personSchema),
        }}
      />
    </div>
  );
}
